import isEqual from 'lodash/isEqual';

const DEFAULTS_ONLY_TABLES = [
  'MAIN_INSTRUMENTS',
  'MOST_IMPORTANT_PORTFOLIO',
  'DOMINANT_INFLUENCES'
];

const EXCLUDED_KEYS = [
  'asset_type',
  'macro_asset_type',
  'transaction_type',
  'risk_measure_type',
  'macro_asset_type_label',
  'sub_asset_type_label',
  'asset_type_label'
];

const MULTI_ROW_BASE_TABLES = [
  'SHARE_CLASSES',
  'PRIME_BROKE_FUNDS',
  'MASTER_AIF_STATUS',
  'CURRENCIES',
  'DOMINANT_INFLUENCES',
  'INVESTOR_GROUP',
  'CONTROLLED_STRUCTURE',
  'ASSUMPTIONS'
];

const isEmptyTable = value => value == null || (Array.isArray(value) && value.length === 0);

const normalizeTable = table => JSON.parse(JSON.stringify(table ?? null));

const isTableChanged = (current, baseline) => {
  if (isEmptyTable(current) && isEmptyTable(baseline)) return false;

  return !isEqual(normalizeTable(current), normalizeTable(baseline));
};

const resolveFields = (tableName, fundSchema, isAIF001) => {
  if (DEFAULTS_ONLY_TABLES.includes(tableName)) {
    return Object.keys(fundSchema.defaultValues);
  }
  const excluded = isAIF001 ? EXCLUDED_KEYS : [...EXCLUDED_KEYS, 'sub_asset_type'];
  return Object.keys(fundSchema.defaultValues).filter(key => !excluded.includes(key));
};

const buildFundData = ({
  fundValue,
  baselineFund,
  schema,
  isAIF001,
  multiRowTables,
  filterNotEmpty,
  filterMultiple
}) => {
  const fundData = {};

  Object.keys(fundValue.fund_data).forEach(tableName => {
    const current = fundValue.fund_data[tableName];

    if (!Array.isArray(current)) return;

    const isChanged = isTableChanged(current, baselineFund?.fund_data?.[tableName]);

    const fundSchema = schema[tableName];
    const fields = resolveFields(tableName, fundSchema, isAIF001);
    const requiredFields = Object.keys(fundSchema.types).filter(
      key => fundSchema.types[key].required
    );

    let result = filterNotEmpty(current, fields, requiredFields);
    if (multiRowTables.has(tableName)) {
      result = filterMultiple(current, fields, Object.keys(fundSchema.types));
    }

    if (!result) return;

    // Persisted rows emptied out must reach the backend even when the table
    // itself is untouched — otherwise the deletion is silently dropped.
    if (!isChanged) {
      const deletedRows = result.filter(row => row?.is_deleted);
      if (deletedRows.length === 0) return;
      fundData[tableName] = deletedRows;
      return;
    }

    fundData[tableName] = result;
  });

  return fundData;
};

export function buildModifiedPayload({
  funds,
  baselineFunds,
  schema,
  isMultiFunds,
  isAIF001,
  currentDataKey,
  strategyTableKeys = [],
  filterNotEmpty,
  filterMultiple
}) {
  const multiRowTables = new Set([...MULTI_ROW_BASE_TABLES, ...strategyTableKeys]);
  const payload = [];

  const pushFund = (fundValue, baselineFund) => {
    const fundData = buildFundData({
      fundValue,
      baselineFund,
      schema,
      isAIF001,
      multiRowTables,
      filterNotEmpty,
      filterMultiple
    });

    if (Object.keys(fundData).length === 0) return;

    payload.push({
      fund_id: fundValue.fund_id,
      fund_name: fundValue.fund_name,
      fund_data: fundData
    });
  };

  if (isMultiFunds && !isAIF001) {
    Object.values(funds)
      .filter(val => val.isSelected)
      .forEach(fundValue => pushFund(fundValue, baselineFunds?.[fundValue.fund_id]));
  } else {
    pushFund(funds[currentDataKey], baselineFunds?.[currentDataKey]);
  }

  return payload;
}
